// MALL PAYMENT TAB — drop this into MallAdminPanel.js as a new tab
// ─────────────────────────────────────────────────────────────
// Add tab button: ['payments', '💳 Payments']
// Add tab content: {tab === 'payments' && <MallPaymentConfig mallId={mallId} />}

import React, { useState, useEffect } from 'react';
import api from '../services/api';

const ALL_PAYMENT_METHODS = [
  { id:'CASH',   icon:'💵', label:'Cash',         desc:'Pay at counter — staff confirms manually'    },
  { id:'CARD',   icon:'💳', label:'Card',         desc:'Visa / Mastercard / Amex — card terminal'   },
  { id:'UPI',    icon:'📱', label:'UPI / QR',     desc:'Scan QR code via phone'                     },
  { id:'WALLET', icon:'⚡', label:'Digital Wallet',desc:'Apple Pay / Google Pay'                    },
  { id:'TAP',    icon:'🔵', label:'Tap Payments', desc:'UAE fast payment gateway'                   },
];

export function MallPaymentConfig({ mallId }) {
  const [config, setConfig] = useState({ CASH:true, CARD:true, UPI:true, WALLET:false, TAP:false });
  const [msg,    setMsg]    = useState('');

  useEffect(() => {
    if (!mallId) return;
    api.get(`/mall/${mallId}/payment-config`)
      .then(r => setConfig(r.data))
      .catch(() => {});
  }, [mallId]);

  const handleToggle = async (method) => {
    const updated = { ...config, [method]: !config[method] };
    setConfig(updated);
    try {
      await api.patch(`/mall/${mallId}/payment-config`, updated);
      setMsg('✅ Saved');
    } catch (_) { setMsg('❌ Save failed'); }
    setTimeout(() => setMsg(''), 2000);
  };

  return (
    <div className="card">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontWeight:700, fontSize:15 }}>💳 Mall Payment Methods</div>
        {msg && <span style={{ fontSize:13, color:msg.startsWith('✅')?'var(--green)':'var(--red)' }}>{msg}</span>}
      </div>
      <div style={{ fontSize:13, color:'var(--text2)', marginBottom:20 }}>
        These settings apply to all vendors in this mall by default.
        Individual vendors can override in their own Vendor Admin panel.
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
        {ALL_PAYMENT_METHODS.map(m => (
          <div key={m.id} style={{
            display:'flex', alignItems:'center', justifyContent:'space-between',
            padding:'14px 16px', background:'var(--bg3)', borderRadius:10,
            border:`1px solid ${config[m.id]?'var(--green)':'var(--border)'}`,
          }}>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <span style={{ fontSize:28 }}>{m.icon}</span>
              <div>
                <div style={{ fontWeight:600 }}>{m.label}</div>
                <div style={{ fontSize:12, color:'var(--text2)' }}>{m.desc}</div>
              </div>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <span style={{ fontSize:12, color:config[m.id]?'var(--green)':'var(--text3)' }}>
                {config[m.id]?'Enabled':'Disabled'}
              </span>
              <div className={`toggle${config[m.id]?' on':''}`}
                onClick={() => handleToggle(m.id)}
                style={{ cursor:'pointer' }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── SUPER ADMIN: AssignForm with mall option ──────────────────
// Replace AssignForm in SuperAdminPanel.js with this version

export function AssignFormWithMall({ models, vendors, malls, initial, onSave, onCancel, loading }) {
  const [assignTo,     setAssignTo]     = useState('vendor'); // 'vendor' | 'mall'
  const [vendorId,     setVendorId]     = useState(initial?.vendorId || '');
  const [mallId,       setMallId]       = useState(initial?.mallId   || '');
  const [modelId,      setModelId]      = useState(initial?.modelId  || (models[0]?.id || ''));
  const [agreedBy,     setAgreedBy]     = useState('');
  const [notes,        setNotes]        = useState('');
  const [useCustom,    setUseCustom]    = useState(false);
  const [customConfig, setCustomConfig] = useState(null);

  useEffect(() => {
    if (!modelId && models.length > 0) setModelId(models[0].id);
  }, [models, modelId]);

  const selectedModel = models.find(m => m.id === modelId);

  const handleSave = () => {
    const data = {
      pricingModelId: modelId,
      agreedBy,
      notes,
      customConfig: useCustom ? customConfig : null,
    };
    if (assignTo === 'vendor') {
      data.vendorId = vendorId;
    } else {
      data.mallId = mallId;
    }
    onSave(data);
  };

  const isValid = modelId && (assignTo === 'vendor' ? vendorId : mallId);

  return (
    <div className="card" style={{ marginBottom:16, border:'1px solid var(--accent)' }}>
      <div style={{ fontWeight:700, marginBottom:16 }}>Assign Pricing Model</div>

      {/* Assign to: vendor or mall */}
      <div style={{ marginBottom:16 }}>
        <div className="section-label" style={{ marginBottom:8 }}>Assign to</div>
        <div style={{ display:'flex', gap:8 }}>
          {[['vendor','🍽 Individual Vendor'],['mall','🏬 Entire Mall']].map(([val,label]) => (
            <div key={val} onClick={() => setAssignTo(val)}
              style={{ flex:1, padding:'10px 14px', borderRadius:8, cursor:'pointer', textAlign:'center',
                border:`2px solid ${assignTo===val?'var(--accent)':'var(--border)'}`,
                background: assignTo===val?'#3d2e10':'var(--bg3)',
                color: assignTo===val?'var(--accent)':'var(--text2)',
                fontWeight:600, fontSize:13 }}>
              {label}
            </div>
          ))}
        </div>
        {assignTo === 'mall' && (
          <div style={{ fontSize:12, color:'var(--text3)', marginTop:8 }}>
            💡 Mall assignment applies to all vendors in that mall unless a vendor has their own override
          </div>
        )}
      </div>

      <div className="grid2" style={{ gap:12, marginBottom:12 }}>
        {/* Vendor or Mall selector */}
        <div>
          <div className="section-label" style={{ marginBottom:6 }}>
            {assignTo === 'vendor' ? 'Vendor *' : 'Mall *'}
          </div>
          {assignTo === 'vendor' ? (
            <select value={vendorId} onChange={e => setVendorId(e.target.value)}>
              <option value="">— Select vendor —</option>
              {vendors.map(v => <option key={v.id} value={v.id}>{v.emoji||'🍽'} {v.name}</option>)}
            </select>
          ) : (
            <select value={mallId} onChange={e => setMallId(e.target.value)}>
              <option value="">— Select mall —</option>
              {malls.map(m => <option key={m.id} value={m.id}>🏬 {m.name}</option>)}
            </select>
          )}
          {assignTo === 'mall' && malls.length === 0 && (
            <div style={{ fontSize:11, color:'var(--red)', marginTop:4 }}>No malls found</div>
          )}
        </div>

        {/* Pricing model */}
        <div>
          <div className="section-label" style={{ marginBottom:6 }}>Pricing Model *</div>
          <select value={modelId} onChange={e => setModelId(e.target.value)}>
            {models.length === 0 && <option value="">— No models —</option>}
            {models.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>

        <div>
          <div className="section-label" style={{ marginBottom:6 }}>Agreed by</div>
          <input type="text" value={agreedBy} onChange={e => setAgreedBy(e.target.value)} placeholder="Your name" />
        </div>
        <div>
          <div className="section-label" style={{ marginBottom:6 }}>Notes</div>
          <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Pilot rate, 60-day trial" />
        </div>
      </div>

      {/* Custom override toggle */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom: useCustom ? 16 : 0 }}>
        <div className={`toggle${useCustom?' on':''}`}
          onClick={() => { setUseCustom(p=>!p); if (!customConfig && selectedModel) setCustomConfig({...selectedModel.config}); }}
          style={{ cursor:'pointer' }} />
        <span style={{ fontSize:13, color:'var(--text2)' }}>
          Override with custom rates for this {assignTo} only
        </span>
      </div>

      {useCustom && selectedModel && customConfig && (
        <div style={{ marginTop:12, padding:'12px 14px', background:'var(--bg3)', borderRadius:8 }}>
          <div className="section-label" style={{ marginBottom:10 }}>Custom configuration</div>
          <ConfigEditorInline type={selectedModel.type} config={customConfig} onChange={setCustomConfig} />
        </div>
      )}

      <div style={{ display:'flex', gap:8, marginTop:16 }}>
        <button className="btn btn-primary" disabled={loading || !isValid} onClick={handleSave}>
          {loading ? '⏳ Assigning...' : 'Assign Model'}
        </button>
        <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// Minimal config editor for the assign form
function ConfigEditorInline({ type, config, onChange }) {
  const update = (key, val) => onChange({ ...config, [key]: val });
  if (type === 'COMMISSION_TIERED') return (
    <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
      <div><div className="section-label" style={{ marginBottom:4 }}>Min fee (AED)</div>
        <input type="number" value={config.minFee||0} style={{ width:80 }} onChange={e => update('minFee',Number(e.target.value))} /></div>
      <div><div className="section-label" style={{ marginBottom:4 }}>Max fee (AED)</div>
        <input type="number" value={config.maxFee||''} style={{ width:80 }} placeholder="None"
          onChange={e => update('maxFee', e.target.value?Number(e.target.value):null)} /></div>
    </div>
  );
  if (type === 'PER_ORDER_TIERED') return (
    <div><div className="section-label" style={{ marginBottom:4 }}>Max fee cap (AED)</div>
      <input type="number" value={config.maxFee||10} style={{ width:80 }} onChange={e => update('maxFee',Number(e.target.value))} /></div>
  );
  if (type === 'FLAT_PLUS_PER_ORDER') return (
    <div style={{ display:'flex', gap:12 }}>
      <div><div className="section-label" style={{ marginBottom:4 }}>Monthly flat (AED)</div>
        <input type="number" value={config.monthlyFlat||2000} style={{ width:120 }} onChange={e => update('monthlyFlat',Number(e.target.value))} /></div>
      <div><div className="section-label" style={{ marginBottom:4 }}>Per order (AED)</div>
        <input type="number" value={config.perOrderFee||1} style={{ width:80 }} step="0.5" onChange={e => update('perOrderFee',Number(e.target.value))} /></div>
    </div>
  );
  return null;
}
